import { useEffect, useMemo, useState } from 'react';
import { Alert, Button, Form, Modal, Table } from 'react-bootstrap';
import { v4 as uuidv4 } from 'uuid';
import { ShippingProviderType, type CloneOrderRequest, type OrderSummary } from '@/infrastructure/OMSClient';
import type { OrderMutationResult } from '@/models/OrderMutationResult';
import { SHIPPING_PROVIDER_LABELS } from '@/pages/oms/orders/utils/shippingProviderLabels';

interface CloneOrderModalProps {
  show: boolean;
  order: OrderSummary;
  operatorName: string;
  onHide: () => void;
  onSubmit: (request: CloneOrderRequest) => Promise<OrderMutationResult>;
}

type ProviderSelection = ShippingProviderType | '';

export function CloneOrderModal({ show, order, operatorName, onHide, onSubmit }: CloneOrderModalProps) {
  const [newOrderId, setNewOrderId] = useState('');
  const [provider, setProvider] = useState<ProviderSelection>('');
  const [reason, setReason] = useState('');
  const [quantities, setQuantities] = useState<Record<string, number>>({});
  const [idempotencyKey, setIdempotencyKey] = useState(uuidv4());
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!show) {
      return;
    }
    setNewOrderId('');
    setProvider('');
    setReason('');
    setQuantities(
      Object.fromEntries(order.lineItems.filter((item) => item.itemId).map((item) => [item.itemId!, item.quantity])),
    );
    setIdempotencyKey(uuidv4());
    setSubmitting(false);
    setError(null);
  }, [order.lineItems, show]);

  const itemIdError = order.lineItems.some((item) => !item.itemId)
    ? 'Every source line item must have an item ID before this order can be cloned safely.'
    : null;
  const priceError = order.lineItems.some((item) => !item.price || item.price <= 0)
    ? 'Every source line item must have a positive unit price before this order can be cloned safely.'
    : null;
  const quantityError = order.lineItems.find((item) => {
    if (!item.itemId) {
      return false;
    }
    const quantity = quantities[item.itemId] ?? Number.NaN;
    return !Number.isInteger(quantity) || quantity < 0 || quantity > item.quantity;
  });
  const totalQuantity = useMemo(
    () => Object.values(quantities).reduce((sum, quantity) => sum + (Number.isFinite(quantity) ? quantity : 0), 0),
    [quantities],
  );
  const validQuantity = !quantityError && totalQuantity >= 1;
  const canSubmit =
    Boolean(newOrderId.trim()) &&
    Boolean(provider) &&
    Boolean(reason.trim()) &&
    !itemIdError &&
    !priceError &&
    validQuantity &&
    !submitting;

  const handleSubmit = async () => {
    if (!canSubmit || !provider) {
      return;
    }
    setSubmitting(true);
    setError(null);

    try {
      const result = await onSubmit({
        originalOrderId: order.orderId,
        newOrderId: newOrderId.trim(),
        deliveryProvider: provider,
        lineItemQuantityOverrides: order.lineItems
          .filter((item) => item.itemId && (quantities[item.itemId] ?? 0) > 0)
          .map((item) => ({ itemId: item.itemId!, quantity: quantities[item.itemId!] ?? 0 })),
        idempotencyKey,
        reason: reason.trim(),
      });

      if (result.success) {
        onHide();
        return;
      }

      setError(result.error ?? 'The clone operation failed. Review the current order state before retrying.');
    } catch (error) {
      setError(
        error instanceof Error
          ? error.message
          : 'The clone operation failed. Review the current order state before retrying.',
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal show={show} onHide={() => !submitting && onHide()} size="lg" backdrop={submitting ? 'static' : true}>
      <Modal.Header closeButton={!submitting}>
        <Modal.Title>Clone Operational Order</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Alert variant="warning">
          The clone is created outside WooCommerce and its shipment is created immediately. Confirm the new ID,
          provider, and allocated quantity before continuing.
        </Alert>
        {(itemIdError || priceError) && <Alert variant="danger">{itemIdError ?? priceError}</Alert>}
        {error && <Alert variant="danger">{error}</Alert>}

        <Form.Group className="mb-3" controlId="clone-order-id">
          <Form.Label>New Order ID *</Form.Label>
          <Form.Control
            value={newOrderId}
            onChange={(event) => setNewOrderId(event.target.value)}
            placeholder={`Enter a unique child ID for ${order.orderId}`}
            disabled={submitting}
          />
          <Form.Text>No suffix is suggested because existing child orders must be checked first.</Form.Text>
        </Form.Group>

        <Form.Group className="mb-3" controlId="clone-provider">
          <Form.Label>Shipping Provider *</Form.Label>
          <Form.Select
            value={provider}
            onChange={(event) => setProvider(event.target.value as ProviderSelection)}
            disabled={submitting}
          >
            <option value="">Select a provider</option>
            <option value={ShippingProviderType.DELEO}>{SHIPPING_PROVIDER_LABELS[ShippingProviderType.DELEO]}</option>
            <option value={ShippingProviderType.YUN}>{SHIPPING_PROVIDER_LABELS[ShippingProviderType.YUN]}</option>
            <option value={ShippingProviderType.PARXL}>{SHIPPING_PROVIDER_LABELS[ShippingProviderType.PARXL]}</option>
          </Form.Select>
        </Form.Group>

        <Form.Label>Line Item Allocation *</Form.Label>
        <Table bordered responsive size="sm">
          <thead>
            <tr>
              <th>SKU</th>
              <th>Source Qty</th>
              <th>Clone Qty</th>
            </tr>
          </thead>
          <tbody>
            {order.lineItems.map((item, index) => (
              <tr key={item.itemId ?? `${item.sku}-${index}`}>
                <td>{item.sku || item.name}</td>
                <td>{item.quantity}</td>
                <td style={{ width: 160 }}>
                  <Form.Control
                    aria-label={`Clone quantity for ${item.sku || item.name}`}
                    type="number"
                    min={0}
                    max={item.quantity}
                    step={1}
                    value={item.itemId ? (quantities[item.itemId] ?? 0) : 0}
                    disabled={submitting || !item.itemId}
                    onChange={(event) => {
                      if (!item.itemId) {
                        return;
                      }
                      setQuantities((current) => ({ ...current, [item.itemId!]: Number(event.target.value) }));
                    }}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
        <div className={validQuantity ? 'text-success mb-3' : 'text-danger mb-3'}>
          Total clone quantity: {totalQuantity}
          {quantityError ? `; ${quantityError.sku || quantityError.name} is outside its source quantity` : ''}
        </div>

        <Form.Group className="mb-3" controlId="clone-reason">
          <Form.Label>Reason *</Form.Label>
          <Form.Control
            as="textarea"
            rows={3}
            maxLength={500}
            value={reason}
            onChange={(event) => setReason(event.target.value)}
            placeholder="Explain why this operational clone is required"
            disabled={submitting}
          />
        </Form.Group>

        <Form.Group>
          <Form.Label>Authenticated Operator</Form.Label>
          <Form.Control value={operatorName} disabled />
        </Form.Group>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide} disabled={submitting}>
          Cancel
        </Button>
        <Button variant="primary" onClick={handleSubmit} disabled={!canSubmit}>
          {submitting ? 'Creating clone and shipment...' : `Create ${totalQuantity}-unit clone`}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
